import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, LogOutIcon, SettingsIcon, ChevronDownIcon, ShieldIcon, UsersIcon, BellIcon, HelpCircleIcon, KeyIcon } from 'lucide-react';
import { useUser } from '../lib/userContext';
export function UserMenu() {
  const {
    currentUser,
    role,
    switchUser
  } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const roleColors = {
    'Org Admin': 'text-purple-400 bg-purple-500/10 border-purple-500/30',
    'Group Admin': 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    'Account Admin': 'text-green-400 bg-green-500/10 border-green-500/30',
    'Account Member': 'text-slate-400 bg-slate-500/10 border-slate-500/30'
  };
  const roleIcons = {
    'Org Admin': ShieldIcon,
    'Group Admin': UsersIcon,
    'Account Admin': KeyIcon,
    'Account Member': UserIcon
  };
  const RoleIcon = roleIcons[role] || UserIcon;
  return <div className="relative" data-id="element-1474">
      {/* Enhanced User Button */}
      <button onClick={() => setIsOpen(!isOpen)} className="group relative flex items-center gap-3 px-4 py-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 rounded-xl transition-all duration-200 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 hover:scale-105" data-id="element-1475">
        {/* Gradient background on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" data-id="element-1476" />
        {/* User Avatar */}
        <div className="relative w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-blue-500/20 group-hover:ring-blue-500/40 group-hover:scale-110 transition-all duration-300" data-id="element-1477">
          {currentUser.name.charAt(0)}
        </div>
        {/* User Info */}
        <div className="relative text-left" data-id="element-1478">
          <div className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors" data-id="element-1479">
            {currentUser.name}
          </div>
          <div className="text-xs text-slate-400" data-id="element-1480">{role}</div>
        </div>
        {/* Chevron */}
        <ChevronDownIcon className={`relative w-4 h-4 text-slate-400 group-hover:text-white transition-all duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1481" />
      </button>
      {/* Enhanced Dropdown */}
      <AnimatePresence data-id="element-1482">
        {isOpen && <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} data-id="element-1483" />
            {/* Dropdown Menu */}
            <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} transition={{
          duration: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="absolute right-0 top-full mt-3 w-80 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 backdrop-blur-xl border border-slate-800/80 rounded-2xl shadow-2xl shadow-black/40 z-50 overflow-hidden ring-1 ring-white/5" data-id="element-1484">
              {/* Header */}
              <div className="relative px-6 py-5 border-b border-slate-800/80 bg-gradient-to-r from-slate-950/50 via-slate-900/50 to-slate-950/50" data-id="element-1485">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-1486" />
                <div className="relative flex items-center gap-4" data-id="element-1487">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold shadow-xl ring-2 ring-blue-500/30" data-id="element-1488">
                    {currentUser.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1489">
                    <div className="text-base font-bold text-white mb-1 truncate" data-id="element-1490">
                      {currentUser.name}
                    </div>
                    <div className="text-xs text-slate-400 truncate" data-id="element-1491">
                      {currentUser.email}
                    </div>
                  </div>
                </div>
                <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border shadow-lg ${roleColors[role]}`} data-id="element-1492">
                  <RoleIcon className="w-3.5 h-3.5" data-id="element-1493" />
                  {role}
                </div>
              </div>
              {/* Menu Items */}
              <div className="relative p-3 space-y-1" data-id="element-1494">
                <button onClick={() => {
              setIsOpen(false);
              const event = new CustomEvent('quad-navigate', {
                detail: {
                  view: 'settings'
                }
              });
              window.dispatchEvent(event);
            }} className="group w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-xl transition-all duration-200 text-left border border-transparent hover:border-slate-700/50 hover:shadow-lg hover:shadow-black/20" data-id="element-1495">
                  <div className="w-9 h-9 rounded-lg bg-slate-800/60 group-hover:bg-blue-500/20 flex items-center justify-center transition-colors" data-id="element-1496">
                    <SettingsIcon className="w-4 h-4 text-slate-400 group-hover:text-blue-400 transition-colors" data-id="element-1497" />
                  </div>
                  <div className="flex-1" data-id="element-1498">
                    <div className="font-semibold" data-id="element-1499">Account Settings</div>
                    <div className="text-xs text-slate-500" data-id="element-1500">
                      Manage your preferences
                    </div>
                  </div>
                </button>
                <button onClick={() => {
              setIsOpen(false);
              // Navigate to notifications
            }} className="group w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-xl transition-all duration-200 text-left border border-transparent hover:border-slate-700/50 hover:shadow-lg hover:shadow-black/20" data-id="element-1501">
                  <div className="w-9 h-9 rounded-lg bg-slate-800/60 group-hover:bg-purple-500/20 flex items-center justify-center transition-colors relative" data-id="element-1502">
                    <BellIcon className="w-4 h-4 text-slate-400 group-hover:text-purple-400 transition-colors" data-id="element-1503" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-slate-900" data-id="element-1504" />
                  </div>
                  <div className="flex-1" data-id="element-1505">
                    <div className="font-semibold" data-id="element-1506">Notifications</div>
                    <div className="text-xs text-slate-500" data-id="element-1507">
                      3 unread updates
                    </div>
                  </div>
                </button>
                <button onClick={() => {
              setIsOpen(false);
              // Navigate to help
            }} className="group w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-xl transition-all duration-200 text-left border border-transparent hover:border-slate-700/50 hover:shadow-lg hover:shadow-black/20" data-id="element-1508">
                  <div className="w-9 h-9 rounded-lg bg-slate-800/60 group-hover:bg-green-500/20 flex items-center justify-center transition-colors" data-id="element-1509">
                    <HelpCircleIcon className="w-4 h-4 text-slate-400 group-hover:text-green-400 transition-colors" data-id="element-1510" />
                  </div>
                  <div className="flex-1" data-id="element-1511">
                    <div className="font-semibold" data-id="element-1512">Help & Support</div>
                    <div className="text-xs text-slate-500" data-id="element-1513">
                      Documentation & guides
                    </div>
                  </div>
                </button>
              </div>
              {/* Divider */}
              <div className="px-6 py-3" data-id="element-1514">
                <div className="h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" data-id="element-1515" />
              </div>
              {/* Role Switcher (Dev Only) */}
              <div className="relative px-6 py-3" data-id="element-1516">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-1517">
                  <ShieldIcon className="w-3.5 h-3.5" data-id="element-1518" />
                  Switch Role (Dev Mode)
                </div>
                <div className="space-y-2" data-id="element-1519">
                  {['Org Admin', 'Group Admin', 'Account Admin', 'Account Member'].map(testRole => <button key={testRole} onClick={() => {
                const userId = testRole === 'Org Admin' ? 'u6' : testRole === 'Group Admin' ? 'u1' : testRole === 'Account Admin' ? 'u2' : 'u3';
                switchUser(userId);
                setIsOpen(false);
              }} className={`group w-full flex items-center justify-between px-4 py-2.5 text-xs rounded-lg transition-all duration-200 border ${role === testRole ? 'bg-blue-500/20 border-blue-500/40 text-blue-400 shadow-lg shadow-blue-500/20' : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:bg-slate-700/40 hover:border-slate-600 hover:text-white'}`} data-id="element-1520">
                      <span className="font-semibold" data-id="element-1521">{testRole}</span>
                      {role === testRole && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-lg shadow-blue-500/50" data-id="element-1522" />}
                    </button>)}
                </div>
              </div>
              {/* Divider */}
              <div className="px-6 py-3" data-id="element-1523">
                <div className="h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" data-id="element-1524" />
              </div>
              {/* Logout */}
              <div className="relative p-3" data-id="element-1525">
                <button onClick={() => {
              setIsOpen(false);
              // Handle logout
              window.location.reload();
            }} className="group w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all duration-200 text-left border border-transparent hover:border-red-500/30 hover:shadow-lg hover:shadow-red-500/20" data-id="element-1526">
                  <div className="w-9 h-9 rounded-lg bg-red-500/10 group-hover:bg-red-500/20 flex items-center justify-center transition-colors" data-id="element-1527">
                    <LogOutIcon className="w-4 h-4" data-id="element-1528" />
                  </div>
                  <div className="flex-1" data-id="element-1529">
                    <div className="font-semibold" data-id="element-1530">Sign Out</div>
                    <div className="text-xs text-red-400/60" data-id="element-1531">
                      End your session
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}